import { memo, useRef, useState } from 'react';
import { useTheme } from 'styled-components/macro';
import { useFileDownload } from 'hooks';
import { truncateString } from 'utils';
import { MediaFile } from '../domain/media-file.types';
import { useMedia } from '../media.context';
import { HStack, SVG } from 'components';
import {
  Button,
  Dropdown,
  Hint,
  message,
  Popconfirm,
  Text,
} from 'components/ui';
import { Icons } from 'assets';
import { MediaPreviewer } from './previewer/media.previewer';
import { MediaUploader } from './media.uploader';

export type MediaThumbnailsProps = {
  emptyText?: string;
  canDelete?: boolean;
  showUploader?: boolean;
  validateFormat?: (mimeType: string) => boolean;
};

export const MediaThumbnails = memo((props: MediaThumbnailsProps) => {
  const { emptyText = 'Отсутствуют файлы', canDelete = true, showUploader = false, validateFormat } = props;
  const { files } = useMedia();

  if (!files.length && !emptyText && !showUploader) {
    return null;
  }

  if (!files.length) {
    return (
      <HStack spacing="xs" wrap="wrap">
        {emptyText && <Text color="gray.400">{emptyText}</Text>}
        {showUploader && <MediaUploader validateFormat={validateFormat} />}
      </HStack>
    );
  }

  return (
    <HStack spacing="xs" wrap="wrap">
      {files.map((file, index) => (
        <File key={file.id} {...file} canDelete={canDelete} index={index} />
      ))}

      {showUploader && <MediaUploader />}

      <MediaPreviewer canDelete={canDelete} />
    </HStack>
  );
});

const File = memo(
  (
    file: MediaFile &
      Pick<MediaThumbnailsProps, 'canDelete'> & { index: number }
  ) => {
    const theme = useTheme();
    const { dispatch, previewer } = useMedia();
    const { download, isLoading } = useFileDownload();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

    const handleDownloadFile = async () => {
      try {
        if (!file.url) {
          throw new Error();
        }

        await download({
          url: file.url,
          filename: file.name,
        });
        message({ type: 'success', text: 'Файл скачан' });
      } catch (error) {
        message({ type: 'error', text: 'Упс... Произошла ошибка' });
      }
    };

    const handleDeleteFile = () => {
      dispatch({
        type: 'MEDIA/FILE_REMOVED',
        file,
      });
      message({ type: 'success', text: 'Файл удалён' });
    };

    return (
      <>
        <Dropdown.Root modal={false}>
          <Dropdown.Trigger asChild disabled={isLoading}>
            <Button
              key={file.id}
              ref={buttonRef}
              mode="neutral"
              size="s"
              pending={isLoading || file.status === 'processing'}
              leftIcon={
                <SVG
                  src={Icons.File}
                  props={{ color: theme.colors.gray[800] }}
                />
              }
            >
              {truncateString(file.name, 30)}
            </Button>
          </Dropdown.Trigger>

          <Dropdown.Content
            align="start"
            style={{
              minWidth: 200,
              maxWidth: 200,
            }}
          >
            <Dropdown.Item onSelect={() => previewer.open(file.index)}>
              <Text>Просмотр</Text>
            </Dropdown.Item>

            <Hint
              content={file.error ?? 'Ошибка при загрузке файла'}
              side="right"
              enabled={file.status === 'error'}
            >
              <Dropdown.Item
                onSelect={handleDownloadFile}
                disabled={file.status === 'error'}
              >
                <Text>Скачать</Text>
              </Dropdown.Item>
            </Hint>

            {file.canDelete && (
              <>
                <Dropdown.Separator />
                <Dropdown.Item $critical onSelect={() => setConfirmOpen(true)}>
                  <Text>Удалить</Text>
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Content>
        </Dropdown.Root>

        <Popconfirm
          anchorRef={buttonRef}
          side="top"
          title="Удаление файла"
          description="Вы уверены, что хотите удалить файл?"
          confirmText="Удалить"
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={handleDeleteFile}
        />
      </>
    );
  }
);
